import { get, writable } from 'svelte/store';
import { parseResponse } from '$lib/client/responseHelpers';

import type { UserNote } from '@api/types';
import { getNote } from '@api/user/user-notes';

import type { FavoriteNotesStore, NotesStoreState } from './types';

const initialValues: NotesStoreState = {
    map: {},
};

function createNotesStore(): FavoriteNotesStore {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<NotesStoreState>) => {
        update((state) => ({
            ...state,
            ...data,
        }));
    };

    const loadByUserId = async (userId: string) => {
        const resp = await getNote(userId);
        const data = parseResponse(resp);

        if (data) {
            updateStore({
                map: {
                    ...get(store).map,
                    [data.id]: data,
                },
            });
        }
    };

    return {
        update,
        subscribe,
        set,
        loadByUserId,
        addNote: (note: UserNote) => {
            updateStore({
                map: {
                    ...get(store).map,
                    [note.id]: note,
                },
            });
        },
        setIsViewed: (userId: string) => {
            update((state) => ({
                map: {
                    ...state.map,
                    [userId]: {
                        ...state.map[userId],
                        isViewed: true,
                    },
                },
            }));
        },
        getNote: (userId: string) => {
            return get(store).map[userId] ?? null;
        },
        removeNote: (userId: string) => {
            updateStore({
                map: Object.entries(get(store).map).reduce((res, [id, note]) => {
                    if (id !== userId) {
                        // eslint-disable-next-line no-param-reassign
                        res[id] = note;
                    }

                    return res;
                }, {} as NotesStoreState['map']),
            });
        },
    };
}

export const userNotesStore = createNotesStore();
